import { useRef, useState, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { addTraveller, removeTraveller } from '../../../redux/travellersSlice';
import { TravellerGroup } from '@/types/types';
import PersonIcon from '../../../assets/icons/person.svg?react';
import useClickOutside from '../../../hooks/useClickOutside';
import arrowDown from '../../../assets/icons/arrow-down.svg';
import minusIcon from '../../../assets/icons/minus.svg';
import plusIcon from '../../../assets/icons/plus.svg';
import styles from './selectors.module.scss';
import getSelectedPlan from '../../../utils/getSelectedPlan';
import usePlan from '@/hooks/usePlan';
import PlanSVG from '../planSVG/PlanSVG';
import usePriceCurrency from '@/hooks/usePriceCurrency';
import usePrices from '@/hooks/usePrices';

interface IProps {
    location?: string;
    mode?: 'standard' | 'compact';
}

const PeopleSelector = ({ location = 'home', mode = 'standard' }: IProps) => {
    const currentPlanId = useAppSelector((state) => state.plan.currentPlan);
    const adults = useAppSelector((state) => state.travellers.adults.length);
    const children = useAppSelector((state) => state.travellers.children.length);
    const babies = useAppSelector((state) => state.travellers.babies.length);
    const totalTravellers = adults + children + babies;
    const selectorRef = useRef<HTMLDivElement>(null);
    const [isActive, setIsActive] = useState(false);
    const { isAloyPlan } = getSelectedPlan(currentPlanId);
    const { t } = useTranslation();
    const isHomePage = location === 'home';
    const isCompact = mode === 'compact';

    const onSelectorClick = () => {
        setIsActive(!isActive);
    };

    useClickOutside(selectorRef, () => {
        if (isActive) setIsActive(false);
    });

    const getSelectorText = () => {
        const adultsText = `${adults} ${adults > 1 ? t('adults') : t('adult')}`;
        const childrenText = `, ${children} ${children > 1 ? t('children') : t('child')}`;
        const babiesText = `, ${babies} ${babies > 1 ? t('babies') : t('baby')}`;

        const selectorText = `${adultsText}${children > 0 ? childrenText : ''}${babies > 0 ? babiesText : ''}`;

        return selectorText.toLowerCase();
    };

    const labelText = isHomePage ? t('selectorTraveler') : t('howManyPersons');
    const selectorText = isHomePage && `${totalTravellers}`;
    const containerStyles = isHomePage
        ? `${styles.selectorContainer} ${styles.peopleSelector} ${styles.peopleSelectorFlykube} ${
              isAloyPlan && styles.peopleSelectorAloy
          }`
        : `${styles.selectorContainer} ${styles.peopleSelector} ${styles.fullWidth}`;

    return (
        <div className={containerStyles} ref={selectorRef}>
            {!isCompact && <p className={styles.selectorName}>{labelText}</p>}
            <button className={styles.selector} onClick={onSelectorClick}>
                <PlanSVG svg={<PersonIcon />} className={styles.icon} />
                {isHomePage && (
                    <p className={`${styles.text} ${isAloyPlan && styles.selectorTextAloyDesktop}`}>{selectorText}</p>
                )}
                {isAloyPlan && <p className={styles.selectorTextAloyMobile}>{getSelectorText()}</p>}
                {!isHomePage && <p className={styles.text}>{getSelectorText()}</p>}
                <div className={styles.arrow}>
                    <img src={arrowDown} alt="" className={isActive ? styles.arrowActive : ''} />
                </div>
            </button>
            {isActive && <SelectorOptions />}
        </div>
    );
};

const AdultsText = () => (
    <Trans i18nKey="selectorAdultText">
        Passengers aged
        <span className={styles.coloured}>12 years</span>
        and over are charged the standard
        <span className={styles.coloured}>adult fare</span>.
    </Trans>
);

const ChildrenText = () => (
    <Trans i18nKey="selectorChildrenText">
        There is a<span className={styles.coloured}>5%</span>
        discount for passengers aged
        <span className={styles.coloured}>2 to 11</span>.
    </Trans>
);

const BabiesText = () => {
    const { getBookingPricePerBaby } = usePrices();
    const { getPriceCurrency } = usePriceCurrency();

    const pricePerBaby = getBookingPricePerBaby();
    const babyPriceCurrency = getPriceCurrency(pricePerBaby);

    return (
        <Trans i18nKey="selectorBabiesText" babyPriceCurrency={babyPriceCurrency}>
            Passengers
            <span className={styles.coloured}>
                under 2 only pay
                {/* @ts-ignore <-- Hasta que i18next solucione problema con el componente Trans */}
                {{ babyPriceCurrency }}
            </span>
            for the FlyKube trip.
        </Trans>
    );
};

const SelectorOptions = () => {
    const adultsNumber = useAppSelector((state) => state.travellers.adults.length);
    const childrenNumber = useAppSelector((state) => state.travellers.children.length);
    const babiesNumber = useAppSelector((state) => state.travellers.babies.length);
    const totalTravellers = adultsNumber + childrenNumber + babiesNumber;
    const { planConfig } = usePlan();
    const { isAllSurprisePlan, isAloyPlan } = getSelectedPlan(planConfig.id);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const options = [
        {
            type: TravellerGroup.adults,
            title: t('adults'),
            text: <AdultsText />,
            number: adultsNumber,
            ariaLabelAdd: t('addAdult'),
            ariaLabelSubstract: t('substractAdult'),
            years: `+ 18 ${t('years')}`,
        },
        {
            type: TravellerGroup.children,
            title: t('children'),
            text: <ChildrenText />,
            number: childrenNumber,
            ariaLabelAdd: t('addChild'),
            ariaLabelSubstract: t('substractChild'),
            years: `2 - 17 ${t('years')}`,
        },
        {
            type: TravellerGroup.babies,
            title: t('babies'),
            text: <BabiesText />,
            number: babiesNumber,
            ariaLabelAdd: t('addBaby'),
            ariaLabelSubstract: t('substractBaby'),
            years: `0 - 2 ${t('years')}`,
        },
    ];

    const onPlusButtonClick = (type: string) => {
        if (isAloyPlan) {
            if (totalTravellers >= 10) return;
            if (type === TravellerGroup.babies && babiesNumber >= adultsNumber) return;
        }
        dispatch(addTraveller(type));
    };

    const onMinusButtonClick = (type: string) => {
        if (type === TravellerGroup.adults) {
            if (adultsNumber <= 1) return;
            dispatch(removeTraveller(type));
        }
        if (type === TravellerGroup.children) {
            if (childrenNumber <= 0) return;
            dispatch(removeTraveller(type));
        }
        if (type === TravellerGroup.babies) {
            if (babiesNumber <= 0) return;
            dispatch(removeTraveller(type));
        }
    };

    const planGradient = `linear-gradient(220deg, ${planConfig.colors.dark}, ${planConfig.colors.light})`;

    return isAloyPlan ? (
        <div className={`${styles.peopleSelectorOptions} ${styles.peopleSelectorOptionsAloy}`}>
            {options.map((option) => {
                let isActive = option.number > 0;
                const onlyOneAdultLeft = option.type === TravellerGroup.adults && option.number <= 1;
                if (onlyOneAdultLeft) isActive = false;

                const activeStyle = isActive ? { background: '#0464A9' } : {};

                return (
                    <Fragment key={option.type}>
                        <div className={styles.selectorOption} key={option.title}>
                            <div className={styles.optionTitleWrapper}>
                                <p className={styles.optionTitle}>{option.title}</p>
                                <p className={styles.optionTitleYears}>{option.years}</p>
                            </div>

                            <div className={styles.optionButtonsContainer}>
                                <button
                                    className={styles.button}
                                    style={activeStyle}
                                    onClick={() => onMinusButtonClick(option.type)}
                                    aria-label={option.ariaLabelSubstract}
                                >
                                    <img src={minusIcon} alt="" />
                                </button>
                                <span
                                    className={`${styles.optionNumber} ${
                                        option.number === 0 && styles.optionNumberZero
                                    }`}
                                >
                                    {option.number}
                                </span>
                                <button
                                    className={styles.button}
                                    style={{ background: '#0464A9' }}
                                    onClick={() => onPlusButtonClick(option.type)}
                                    aria-label={option.ariaLabelAdd}
                                >
                                    <img src={plusIcon} alt="" />
                                </button>
                            </div>
                        </div>
                    </Fragment>
                );
            })}
        </div>
    ) : (
        <div
            className={`${styles.peopleSelectorOptions} ${
                isAllSurprisePlan && styles.peopleSelectorOptionsAllSurprise
            }`}
        >
            {options.map((option) => {
                let isActive = option.number > 0;
                const onlyOneAdultLeft = option.type === TravellerGroup.adults && option.number <= 1;
                if (onlyOneAdultLeft) isActive = false;

                const activeStyle = isActive ? { background: planGradient } : {};

                return (
                    <Fragment key={option.type}>
                        <p className={styles.optionTitle}>{option.title}</p>
                        <div className={styles.selectorOption} key={option.title}>
                            <div className={styles.optionButtonsContainer}>
                                <button
                                    className={styles.button}
                                    style={activeStyle}
                                    onClick={() => onMinusButtonClick(option.type)}
                                    aria-label={option.ariaLabelSubstract}
                                >
                                    <img src={minusIcon} alt="" />
                                </button>
                                <span className={styles.optionNumber}>{option.number}</span>
                                <button
                                    className={styles.button}
                                    style={{ background: planGradient }}
                                    onClick={() => onPlusButtonClick(option.type)}
                                    aria-label={option.ariaLabelAdd}
                                >
                                    <img src={plusIcon} alt="" />
                                </button>
                            </div>
                            <p className={styles.optionText}>{option.text}</p>
                        </div>
                    </Fragment>
                );
            })}
        </div>
    );
};

export default PeopleSelector;
